// Worth a look, a page of its own (SCENARIOS E1, DIRECTION U27,
// PLAN-2026-10-27-worth-a-look.md): the players on no team in a league who
// have scored the most lately, at each position it starts, and the ones whose
// starter is out or doubtful. `GET /api/leagues/:id/worth-a-look`.
//
// FACTS WITH THEIR REASONS, never a sentence saying to add anybody
// (docs/decisions/2026-09-12-tools-not-instructions.md): each item is the
// Players list's own row and why it is here, and a client puts that into
// words.

import { z } from 'zod';
import { PlayerRowView, PlayerSummary, PositionFilterView } from './views';

/** At most this many a position: a shortlist, and the Players list is the long one. */
export const WORTH_PER_POSITION = 3;

/** At most this many players next up behind a starter who is out or doubtful. */
export const WORTH_NEXT_UP_MAX = 5;

/** How many of the league's last weeks "lately" is over. */
export const WORTH_WEEKS = 2;

/** One of the weeks "lately" is over. */
export const WorthWeek = z.object({
  period: z.number().int().min(1),
  label: z.string(),
});
export type WorthWeek = z.infer<typeof WorthWeek>;

/**
 * His points in one of those weeks, in this league's scoring. `kind` says
 * why there are none: `bye` when his club had no game, `none` when it played
 * and he has no line.
 */
export const WorthWeekPoints = z.object({
  period: z.number().int().min(1),
  points: z.number().nullable(),
  kind: z.enum(['played', 'bye', 'none']),
});
export type WorthWeekPoints = z.infer<typeof WorthWeekPoints>;

/**
 * Why a player is here.
 *
 * - `lately`: among the three with the most points a game over `weeks` at his
 *   first position, `rank` 1 the most.
 * - `next-up`: the first string at his place on his club's depth chart
 *   (`column`, the chart's own word) is ruled out or doubtful this week, and
 *   he is the first below that player who is not. `ahead` is that player;
 *   `aheadAvg` his points a game this season in this league's scoring, null
 *   before his first.
 */
export const WorthReason = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('lately'),
    rank: z.number().int().min(1).max(WORTH_PER_POSITION),
  }),
  z.object({
    kind: z.literal('next-up'),
    column: z.string(),
    ahead: PlayerSummary,
    aheadAvg: z.number().nullable(),
  }),
]);
export type WorthReason = z.infer<typeof WorthReason>;

export const WorthItem = z.object({
  /** The Players row, exactly as the pool serves it: status, game, figures, availability, action. */
  row: PlayerRowView,
  /** His points in each of the view's `weeks`, in the same order. */
  weeks: z.array(WorthWeekPoints).max(WORTH_WEEKS),
  /** Points a game over the weeks of `weeks` he has a line in; null with none. */
  lately: z.number().nullable(),
  because: WorthReason,
});
export type WorthItem = z.infer<typeof WorthItem>;

/** One position the league starts, and its players with the most points lately. */
export const WorthPosition = PositionFilterView.pick({ id: true, label: true, name: true }).extend({
  items: z.array(WorthItem).max(WORTH_PER_POSITION),
});
export type WorthPosition = z.infer<typeof WorthPosition>;

export const WorthALookView = z.object({
  /**
   * The league's last weeks that have ended, oldest first, at most
   * `WORTH_WEEKS`: what `lately` is over. Empty before a week has ended, and
   * then every position's `items` is too.
   */
  weeks: z.array(WorthWeek).max(WORTH_WEEKS),
  /**
   * Every position the league's lineup starts, in the sport's order, each by
   * its own name (a flex is not one: its players are its positions'). A
   * position with nobody on no team who scored is here with no items.
   */
  positions: z.array(WorthPosition),
  /** Players next up behind a starter who is out or doubtful, the biggest role first. */
  nextUp: z.array(WorthItem).max(WORTH_NEXT_UP_MAX),
  /**
   * The league turned suggestions off (`LeagueRef.tools`, RULES_SCHEMA §15c):
   * no lists, an answer rather than a refusal, so the page can say so rather
   * than read as nobody being worth a look. Added 2026-10-29.
   */
  off: z.boolean().default(false),
});
export type WorthALookView = z.infer<typeof WorthALookView>;
